'use client'

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { InfoIcon, CheckIcon, Loader2 } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { DatePicker } from '@/components/ui/date-picker'
import { useApiKeysList, useCreateApiKey } from '@/lib/api/apiKeys'
import { useToast } from '@/components/ui/use-toast'

// ============================================================================
// Types & Schema
// ============================================================================

const chargeblastFormSchema = z.object({
  accessToken: z.string().min(1, 'Access token is required'),
})

const createTokenFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable(),
  expiresAt: z.date().nullable(),
  orgRole: z.string(),
  permissions: z.array(z.string()),
  revoked: z.boolean(),
  revokedAt: z.date().nullable(),
})

type FormValues = z.infer<typeof chargeblastFormSchema>
type CreateTokenFormValues = z.infer<typeof createTokenFormSchema>

interface ChargeblastFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ChargeblastForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: ChargeblastFormProps) {
  const { toast } = useToast()
  const { t } = useTranslation('integrations')
  const [selectedAccessToken, setSelectedAccessToken] = useState<string | undefined>(
    defaultValues.credentials?.accessToken
  )
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  // Fetch existing API keys
  const { data: apiKeysData, isLoading: isLoadingKeys, refetch: refetchKeys } = useApiKeysList({
    pagination: {
      page: 1,
      pageSize: 100,
    },
  })

  // Create API key mutation
  const createApiKeyMutation = useCreateApiKey()

  // Main form
  const form = useForm<FormValues>({
    resolver: zodResolver(chargeblastFormSchema),
    defaultValues: {
      accessToken: defaultValues.credentials?.accessToken ?? '',
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  // Create token form
  const createTokenDefaultValues = useMemo(
    () => ({
      name: '',
      description: null,
      expiresAt: null,
      permissions: [],
      orgRole: 'org:admin',
      revoked: false,
      revokedAt: null,
    }) as CreateTokenFormValues,
    []
  )

  const createTokenForm = useForm<CreateTokenFormValues>({
    resolver: zodResolver(createTokenFormSchema),
    defaultValues: createTokenDefaultValues,
  })

  const {
    handleSubmit: handleCreateSubmit,
    getValues: getCreateValues,
    setValue: setCreateValue,
    register: registerCreate,
    reset: resetCreateForm,
    watch: watchCreate,
    formState: { errors: createErrors },
  } = createTokenForm

  // Update selected token when it changes
  useEffect(() => {
    if (selectedAccessToken) {
      setValue('accessToken', selectedAccessToken)
    }
  }, [selectedAccessToken, setValue])

  // Set newly created token as selected
  useEffect(() => {
    if (newlyCreatedToken) {
      setSelectedAccessToken(newlyCreatedToken)
      setValue('accessToken', newlyCreatedToken)
    }
  }, [newlyCreatedToken, setValue])

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      credentials: {
        accessToken: data.accessToken,
      },
    })
  }

  const handleCreateToken = async () => {
    const data = getCreateValues()
    try {
      const response = await createApiKeyMutation.mutateAsync(data)

      // Store the newly created token (shown only once!)
      setNewlyCreatedToken(response.token)
      setShowCreateForm(false)

      // Reset create form
      resetCreateForm(createTokenDefaultValues)

      // Refetch the list
      refetchKeys()
    } catch (error) {
      console.error('Error creating access token:', error)
    }
  }

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      setCopiedToken(token)
      toast({
        title: t('toasts.successTitle'),
        description: t('toasts.tokenCopied'),
      })
      setTimeout(() => setCopiedToken(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.failedToCopyToken'),
        variant: 'destructive',
      })
    }
  }

  const maskToken = (token: string) => {
    if (!token) return ''
    return token.slice(0, 4) + token.slice(4).replace(/[a-zA-Z0-9]/gi, '*')
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Setup Instructions */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('chargeblast.setupInstructions')}</AlertTitle>
        <AlertDescription>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-sm">
            <li>{t('chargeblast.setupStep1')}</li>
            <li dangerouslySetInnerHTML={{ __html: t('chargeblast.setupStep2') }}></li>
            <li>{t('chargeblast.setupStep3')}</li>
          </ol>
        </AlertDescription>
      </Alert>

      {/* Select Existing Token */}
      <Card>
        <CardHeader>
          <CardTitle>{t('chargeblast.selectAccessToken')}</CardTitle>
          <CardDescription>{t('chargeblast.selectAccessTokenDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
        {isLoadingKeys ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
              <div className="space-y-2">
                <Label htmlFor="accessToken">
                  {t('chargeblast.accessToken')}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
            <Select
              value={selectedAccessToken}
                  onValueChange={(value) => setSelectedAccessToken(value)}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('chargeblast.selectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {apiKeysData?.apiKeys && apiKeysData.apiKeys.length > 0 ? (
                  <>
                    {apiKeysData.apiKeys.map((apiKey, index) => (
                      <SelectItem key={apiKey.name + "_" + index} value={apiKey.token}>
                        {apiKey.name}: {maskToken(apiKey.token)}
                      </SelectItem>
                    ))}
                  </>
                ) : null}
              </SelectContent>
            </Select>
            {errors.accessToken && (
              <p className="text-xs text-destructive">{errors.accessToken.message}</p>
            )}
              </div>

            {selectedAccessToken && !showCreateForm && (
              <Button
                type="button"
                  variant="outline"
                onClick={() => handleCopyToken(selectedAccessToken)}
              >
                {copiedToken === selectedAccessToken ? (
                  <>
                    <CheckIcon className="mr-2 h-4 w-4" />
                    {t('chargeblast.copied')}
                  </>
                ) : (
                  t('chargeblast.copyToClipboard')
                )}
              </Button>
            )}
          </>
        )}
        </CardContent>
      </Card>

      {/* Create New Token */}
      <Card>
        <CardHeader>
          <CardTitle>{t('chargeblast.createNewToken')}</CardTitle>
          <CardDescription>{t('chargeblast.createNewTokenDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
        {!newlyCreatedToken && !showCreateForm && (
            <Button type="button" variant="outline" onClick={() => setShowCreateForm(true)}>
              {t('chargeblast.createNewButton')}
          </Button>
        )}

        {showCreateForm && (
          <form
            id="api-key-form"
            onSubmit={handleCreateSubmit(handleCreateToken) as React.FormEventHandler<HTMLFormElement>}
            className="space-y-4"
          >
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('chargeblast.name')}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground">
                      {t('chargeblast.nameHint')}
                    </p>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('chargeblast.namePlaceholder')}
                  {...registerCreate('name')}
                />
                {createErrors.name && (
                  <p className="text-xs text-destructive">{createErrors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('chargeblast.descriptionOptional')}</Label>
                <Input
                  id="description"
                  type="text"
                  placeholder={t('chargeblast.descriptionPlaceholder')}
                  {...registerCreate('description')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiresAt">{t('chargeblast.expirationOptional')}</Label>
                <DatePicker
                  value={watchCreate('expiresAt') || undefined}
                  onChange={(date) => setCreateValue('expiresAt', date || null)}
                  disabledDays={{
                    before: new Date(),
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="orgRole">{t('chargeblast.role')}</Label>
                <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('chargeblast.roleHint') }} />
                <Select
                  value={watchCreate('orgRole')}
                  onValueChange={(value) => setCreateValue('orgRole', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('chargeblast.selectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org:admin">{t('chargeblast.admin')}</SelectItem>
                    <SelectItem value="org:support">{t('chargeblast.support')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
              <Button type='submit' disabled={createApiKeyMutation.isPending}>
                  {createApiKeyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('chargeblast.create')}
              </Button>
                <Button type="button" variant="ghost" onClick={() => setShowCreateForm(false)}>
                  {t('chargeblast.cancel')}
                </Button>
            </div>
          </form>
        )}

        {/* Newly Created Token Result */}
        {newlyCreatedToken && (
            <div className="space-y-2">
              <Label>{t('chargeblast.newAccessToken')}</Label>
              <div className="flex items-center gap-2">
                <Input value={newlyCreatedToken} disabled className="flex-1 font-mono text-sm" />
            <Button
              type="button"
                  variant="outline"
              onClick={() => handleCopyToken(newlyCreatedToken)}
            >
              {copiedToken === newlyCreatedToken ? (
                  <>
                    <CheckIcon className="mr-2 h-4 w-4" />
                    {t('chargeblast.copied')}
                  </>
              ) : (
                    t('chargeblast.copy')
              )}
            </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('chargeblast.tokenCopyWarning')}
              </p>
          </div>
        )}
        </CardContent>
      </Card>
    </form>
  )
}
